import { ApiPromise } from "@services/api";
import { useCallback } from "react";

import { useAjaxState } from "./use_ajax_state";
import { useGetCsrfToken } from "./use_get_csrf_token";

const useMutate = <ReturnType, InputType>(
  apiCall: (token: string) => (inputData: InputType) => ApiPromise<ReturnType>,
) => {
  const { getCsrfToken } = useGetCsrfToken();

  const {
    data,
    setData,
    loading,
    error,
    setError,
    startingRequest,
    requestFinished,
  } = useAjaxState<ReturnType>();

  const submitData = useCallback(
    async (inputData: InputType) => {
      const csrfToken = getCsrfToken();
      if (!csrfToken) {
        throw Error("Can't find csrfToken!");
      }
      try {
        startingRequest();
        const response = await apiCall(csrfToken)(inputData);
        requestFinished();
        setData(response.data);
        setError(response.errors);
        return response.data;
      } catch (error) {
        setError([error]);
      } finally {
        requestFinished();
      }
    },
    [
      apiCall,
      getCsrfToken,
      startingRequest,
      requestFinished,
      setData,
      setError,
    ],
  );

  return {
    submitData,
    loading,
    data,
    error,
  };
};

export { useMutate };
